import { BaseInterior } from './BaseInterior';
import { socketManager } from '../socket/SocketManager';

const BUSINESS_TYPE_NAMES: Record<string, string> = {
  EPICERIE: 'Epicerie',
  PIZZERIA: 'Pizzeria',
  LAVERIE: 'Laverie',
  BOITE_NUIT: 'Boite de Nuit',
};

export class BusinessInterior extends BaseInterior {
  render(): void {
    this.drawRoom(0x4a3a2a, 0x6b5b4b);
    this.addExitButton();

    const bi = this.stateData?.businessInfo;
    const typeName = bi ? (BUSINESS_TYPE_NAMES[bi.businessType] ?? bi.businessType) : 'Commerce';
    this.addTitle(`${this.building.name} - ${typeName}`);

    const { width, height } = this.scene.scale;

    if (!bi) {
      this.addLabel(80, height / 2 - 20, 'Commerce pas encore configure.', '#ff9800', '14px');
      this.addLabel(80, height / 2 + 10, 'Achetez ce batiment pour commencer.', '#aaaaaa', '12px');
      this.drawFurniture(width);
      return;
    }

    if (bi.isClosedUntil) {
      this.addLabel(80, 100, 'FERME PAR LA POLICE', '#ff4444', '24px');
      this.addLabel(80, 140, `Reouverture: ${new Date(bi.isClosedUntil).toLocaleTimeString()}`, '#aaaaaa', '14px');
      this.drawFurniture(width);
      return;
    }

    let y = 100;
    const leftCol = 80;

    // Legitimacy slider
    this.addLabel(leftCol, y, `Activite reelle: ${bi.legitimacyCursor}%`, '#ffffff', '14px');
    y += 20;
    this.drawProgressBar(leftCol, y, 200, 14, bi.legitimacyCursor / 100, 0x333333, 0x4caf50);
    y += 25;

    this.addButton(leftCol + 50, y, '[ - ]', '#ff9800', () => {
      socketManager.sendAction('SET_LEGITIMACY_CURSOR', {
        businessId: bi.id,
        value: Math.max(0, bi.legitimacyCursor - 10),
      });
      this.refreshAfterAction();
    });
    this.addButton(leftCol + 130, y, '[ + ]', '#4caf50', () => {
      socketManager.sendAction('SET_LEGITIMACY_CURSOR', {
        businessId: bi.id,
        value: Math.min(100, bi.legitimacyCursor + 10),
      });
      this.refreshAfterAction();
    });
    y += 40;

    // Ratio + capacity
    this.addLabel(leftCol, y, `Ratio blanchiment: ${(bi.currentRatio * 100).toFixed(0)}%`, '#69db7c', '14px');
    y += 22;
    this.addLabel(leftCol, y, `Capacite: $${bi.dailyCapacity}/jour`, '#aaaaaa', '12px');
    y += 22;

    // Reputation
    const stars = Math.round(bi.reputationScore / 20);
    const starStr = '*'.repeat(stars) + '.'.repeat(5 - stars);
    this.addLabel(leftCol, y, `Reputation: ${starStr} (${bi.reputationScore.toFixed(0)}/100)`, '#ffeaa7', '12px');
    y += 22;

    // Suspicion
    this.addLabel(leftCol, y, 'Suspicion:', '#aaaaaa', '12px');
    this.drawProgressBar(leftCol + 90, y + 2, 150, 12, bi.suspicionLevel / 100, 0x333333,
      bi.suspicionLevel < 40 ? 0x4caf50 : bi.suspicionLevel < 70 ? 0xff9800 : 0xff4444);
    this.addLabel(leftCol + 250, y, `${bi.suspicionLevel.toFixed(0)}%`, '#cccccc', '11px');
    y += 35;

    // Laundering section
    this.addLabel(leftCol, y, '--- BLANCHIMENT ---', '#ff6b35', '14px');
    y += 25;

    this.addButton(leftCol + 80, y, '[ Deposer $100 ]', '#ff6b6b', () => {
      socketManager.sendAction('DEPOSIT_DIRTY_CASH', { businessId: bi.id, amount: 100 });
      this.refreshAfterAction();
    });
    y += 35;

    this.addButton(leftCol + 80, y, '[ Deposer $500 ]', '#ff6b6b', () => {
      socketManager.sendAction('DEPOSIT_DIRTY_CASH', { businessId: bi.id, amount: 500 });
      this.refreshAfterAction();
    });
    y += 35;

    this.addButton(leftCol + 80, y, '[ Deposer $1000 ]', '#ff6b6b', () => {
      socketManager.sendAction('DEPOSIT_DIRTY_CASH', { businessId: bi.id, amount: 1000 });
      this.refreshAfterAction();
    });
    y += 40;

    // Queue display
    if (bi.launderingQueue.length > 0) {
      this.addLabel(leftCol, y, 'File de conversion:', '#aaaaaa', '12px');
      y += 20;

      for (const q of bi.launderingQueue) {
        const status = q.isReady ? 'PRET' : `${(q.progress * 100).toFixed(0)}%`;
        const color = q.isReady ? '#4caf50' : '#ffb347';
        this.addLabel(leftCol + 10, y,
          `$${q.dirtyCashDeposited} -> $${q.expectedCleanCash.toFixed(0)} [${status}]`, color, '11px');

        if (q.isReady) {
          this.addButton(leftCol + 250, y, '[ Recuperer ]', '#4caf50', () => {
            socketManager.sendAction('COLLECT_CLEAN_MONEY', { queueId: q.id });
            this.refreshAfterAction();
          });
        }
        y += 22;
      }
    } else {
      this.addLabel(leftCol, y, 'Aucune conversion en cours', '#666666', '11px');
    }

    this.drawFurniture(width);
  }

  private drawFurniture(rightX: number): void {
    const rx = rightX - 220;
    this.graphics.fillStyle(0x8d6e63, 1);
    this.graphics.fillRect(rx, 120, 150, 50);
    this.graphics.fillStyle(0x5d4037, 1);
    this.graphics.fillRect(rx + 10, 200, 40, 40);
    this.graphics.fillRect(rx + 70, 200, 40, 40);
    this.graphics.fillStyle(0x333333, 1);
    this.graphics.fillRect(rx + 50, 125, 40, 30);
    this.graphics.fillStyle(0x4caf50, 1);
    this.graphics.fillRect(rx + 55, 130, 30, 8);
  }

  private refreshAfterAction(): void {
    socketManager.onceActionResult(() => {
      socketManager.sendAction('ENTER_BUILDING', { buildingId: this.building.id });
    });
  }

  bindEvents(): void {
    // No persistent handlers
  }
}
